import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Search, ChevronRight, Phone, Mail, MapPin, X, IndianRupee, CalendarDays, ShieldCheck, FileText, AlertCircle, Bug, Crown, TrendingUp, Activity } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function ServedCustomersTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const [err, setErr] = useState('');

  const minusDays = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const today = () => new Date().toISOString().slice(0, 10);
  const range = useMemo(() => {
    if (dateFilter === 'today') return { date_from: today(), date_to: today() };
    if (dateFilter === '7d') return { date_from: minusDays(7), date_to: today() };
    if (dateFilter === '30d') return { date_from: minusDays(30), date_to: today() };
    if (dateFilter === 'custom') return { date_from: dateFrom, date_to: dateTo };
    return {};
  }, [dateFilter, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { ...range };
      if (q.trim()) params.q = q.trim();
      if (plan !== 'all') params.plan = plan;
      const r = await api('GET', '/admin/served-customers', { params });
      setRows(r.data?.items || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [api, q, plan, range]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="served-customers-tab">
      <div className="flex items-center gap-2">
        <Users size={20} className="text-emerald-700" />
        <h2 className="text-xl font-bold">Served Customers</h2>
        <span className="text-xs text-slate-500">— Customers whose service has been completed (one-time or AMC)</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or email…" className="pl-9 h-10" data-testid="srv-search" />
        </div>
        <div className="inline-flex rounded-full border border-slate-300 bg-white p-0.5">
          {[['all', 'All'], ['one_time', 'One-Time'], ['amc', 'AMC']].map(([k, l]) => (
            <button key={k} onClick={() => setPlan(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${plan === k ? 'bg-emerald-600 text-white' : 'text-slate-600'}`} data-testid={`srv-plan-${k}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {[['all', 'All'], ['today', 'Today'], ['7d', '7d'], ['30d', '30d'], ['custom', 'Custom']].map(([k, l]) => (
            <button key={k} onClick={() => setDateFilter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${dateFilter === k ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`} data-testid={`srv-date-${k}`}>{l}</button>
          ))}
          {dateFilter === 'custom' && (<>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-auto" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-auto" />
          </>)}
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}

      {loading ? <div className="text-center py-10 text-slate-400">Loading…</div> :
        rows.length === 0 ? <div className="text-center py-10 text-slate-400 text-sm">No served customers yet.</div> :
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Plan</th>
                <th className="text-right px-3 py-2">Services</th>
                <th className="text-right px-3 py-2">Total Paid</th>
                <th className="text-right px-3 py-2">Tickets</th>
                <th className="text-left px-3 py-2">Last Service</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.key} onClick={() => setOpenKey(c.key)} className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer" data-testid={`srv-row-${c.key}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold text-slate-900">{c.customer_name}</div>
                    <div className="text-[11px] text-slate-500">{c.customer_phone}{c.customer_email && <> · {c.customer_email}</>}</div>
                  </td>
                  <td className="px-3 py-2">
                    {c.plan_type === 'amc' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Crown size={10} /> AMC</span>
                    ) : (
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">One-Time</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{c.services_count}</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmtMoney(c.total_paid)}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {c.open_tickets > 0 && <span className="text-rose-600 font-bold">{c.open_tickets} open</span>}
                    {c.open_tickets === 0 && c.total_tickets > 0 && <span className="text-slate-500">{c.total_tickets} closed</span>}
                    {c.total_tickets === 0 && <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(c.last_service_date)} · {c.last_pest || '—'}</td>
                  <td className="px-2 text-slate-400"><ChevronRight size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {openKey && <CustomerProfileDrawer api={api} customerKey={openKey} onClose={() => setOpenKey(null)} />}
    </div>
  );
}

// =====================================================
// CUSTOMER PROFILE DRAWER — right-side slide-in (full 360 view)
// =====================================================
function CustomerProfileDrawer({ api, customerKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [invOpen, setInvOpen] = useState(null); // selected invoice for the nested slide-in
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const r = await api('GET', `/admin/served-customers/${encodeURIComponent(customerKey)}/profile`);
        if (alive) setData(r.data);
      } catch (e) { if (alive) setErr(e.response?.data?.detail || e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [api, customerKey]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="srv-profile-drawer">
      <div className="absolute right-0 top-0 h-full w-full sm:w-[640px] bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white flex items-center justify-between">
          <div>
            <div className="text-xs opacity-90">Served Customer · 360°</div>
            <div className="text-lg font-bold">{data?.customer?.name || 'Loading…'}</div>
            {data?.customer?.phone && <div className="text-xs opacity-90 mt-0.5"><Phone size={11} className="inline mr-1" />{data.customer.phone} {data.customer.email && <>· <Mail size={11} className="inline mx-1" />{data.customer.email}</>}</div>}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="srv-profile-close"><X size={18} /></button>
        </div>

        {loading && <div className="flex-1 flex items-center justify-center text-slate-400">Loading…</div>}
        {err && <div className="m-3 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{err}</div>}

        {data && !loading && (
          <>
            {/* Stats strip */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-slate-50 border-b border-slate-200 text-center">
              <Stat icon={<IndianRupee size={14} />} label="Total Paid" value={fmtMoney(data.stats.total_paid)} tone="emerald" />
              <Stat icon={<Activity size={14} />} label="Services" value={data.stats.services_count} tone="indigo" />
              <Stat icon={<ShieldCheck size={14} />} label="Warranties" value={data.stats.warranties_count} tone="amber" />
              <Stat icon={<AlertCircle size={14} />} label="Open Tkts" value={data.stats.open_tickets} tone={data.stats.open_tickets ? 'rose' : 'slate'} />
            </div>

            {data.customer.address && (
              <div className="px-3 py-2 text-xs text-slate-600 bg-amber-50/50 border-b border-amber-100"><MapPin size={11} className="inline mr-1" />{data.customer.address}</div>
            )}

            {/* Tabs */}
            <div className="flex gap-0.5 px-3 pt-2 bg-slate-50 border-b border-slate-200">
              {[['overview', 'Timeline'], ['bookings', `Bookings (${data.bookings.length})`], ['invoices', `Invoices (${data.invoices.length})`], ['warranties', `Warranties (${data.warranties.length})`], ['tickets', `Tickets (${data.tickets.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-xs font-semibold rounded-t-lg ${tab === k ? 'bg-white border border-b-0 border-slate-200 text-emerald-700' : 'text-slate-500 hover:text-slate-800'}`} data-testid={`srv-profile-tab-${k}`}>{l}</button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {tab === 'overview' && (
                <div className="space-y-2">
                  {data.timeline.length === 0 && <div className="text-slate-400 text-sm">No activity yet.</div>}
                  {data.timeline.map((e, i) => <TimelineRow key={i} event={e} />)}
                </div>
              )}
              {tab === 'bookings' && data.bookings.map((b) => (
                <Card key={b.id}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold">{b.booking_code}</span>
                    <StatusChip status={b.status} />
                  </div>
                  <div className="text-sm mt-1">{b.service_label} · {fmtDate(b.scheduled_date)}</div>
                  <div className="text-xs text-slate-500">{b.customer_city} · ₹{(b.final_amount || 0).toLocaleString('en-IN')}</div>
                </Card>
              ))}
              {tab === 'invoices' && data.invoices.map((inv) => (
                <Card key={inv.id} onClick={() => setInvOpen(inv)} className="cursor-pointer hover:border-emerald-400" testid={`srv-inv-${inv.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold">{inv.invoice_number}</span>
                    <StatusChip status={inv.payment_status} />
                  </div>
                  <div className="flex justify-between items-end mt-1">
                    <div>
                      <div className="text-sm">{inv.service_name || '—'}</div>
                      <div className="text-xs text-slate-500">{fmtDate(inv.invoice_date)} · {inv.is_gst ? 'GST' : 'Estimate'}</div>
                    </div>
                    <div className="text-base font-bold text-emerald-700">{fmtMoney(inv.total_amount)}</div>
                  </div>
                </Card>
              ))}
              {tab === 'warranties' && data.warranties.map((w) => (
                <Card key={w.id}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold">{w.warranty_number}</span>
                    <StatusChip status={w.status} />
                  </div>
                  <div className="text-sm mt-1">{w.pest_name} · {w.warranty_value} {w.warranty_unit}</div>
                  <div className="text-xs text-slate-500">Valid until {fmtDate(w.end_date)}</div>
                </Card>
              ))}
              {tab === 'tickets' && (
                <>
                  {data.tickets.length === 0 && <div className="text-slate-400 text-sm">No tickets raised for this customer.</div>}
                  {data.tickets.map((t) => (
                    <Card key={t.id}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-bold">{t.ticket_no}</span>
                        <StatusChip status={t.status} />
                      </div>
                      <div className="text-sm mt-1"><Bug size={12} className="inline mr-1" />{t.pest_label || t.pest_type}</div>
                      <div className="text-xs text-slate-500 line-clamp-2">{t.description}</div>
                      <div className="text-xs mt-1">Charge: <b>{fmtMoney(t.charge_amount)}</b> · {t.charge_status}</div>
                    </Card>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {invOpen && <InvoiceInfoPanel invoice={invOpen} onClose={() => setInvOpen(null)} />}
    </div>
  );
}

function Stat({ icon, label, value, tone = 'emerald' }) {
  const tones = { emerald: 'text-emerald-700', indigo: 'text-indigo-700', amber: 'text-amber-700', rose: 'text-rose-700', slate: 'text-slate-700' };
  return (
    <div className="bg-white rounded-md p-2 border border-slate-200">
      <div className={`text-[10px] uppercase font-bold tracking-wider ${tones[tone]} flex items-center justify-center gap-1`}>{icon} {label}</div>
      <div className="text-base font-bold mt-0.5">{value}</div>
    </div>
  );
}

function Card({ children, className = '', onClick, testid }) {
  return <div className={`bg-white border border-slate-200 rounded-lg p-3 ${className}`} onClick={onClick} data-testid={testid}>{children}</div>;
}

function StatusChip({ status }) {
  const map = {
    completed: 'bg-emerald-100 text-emerald-700',
    warranty_active: 'bg-emerald-100 text-emerald-700',
    scheduled: 'bg-blue-100 text-blue-700',
    paid: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-rose-100 text-rose-700',
    stopped: 'bg-amber-100 text-amber-800',
    expired: 'bg-slate-100 text-slate-600',
    open: 'bg-amber-100 text-amber-800',
    assigned: 'bg-indigo-100 text-indigo-700',
    in_progress: 'bg-blue-100 text-blue-700',
    awaiting_payment: 'bg-orange-100 text-orange-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    force_closed: 'bg-slate-200 text-slate-700',
    active: 'bg-emerald-100 text-emerald-700',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700';
  return <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${cls}`}>{(status || '').replace(/_/g, ' ')}</span>;
}

function TimelineRow({ event }) {
  const kindMeta = {
    booking: { Icon: CalendarDays, tone: 'bg-blue-50 border-blue-200 text-blue-800' },
    invoice: { Icon: FileText, tone: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
    warranty: { Icon: ShieldCheck, tone: 'bg-amber-50 border-amber-200 text-amber-800' },
    ticket: { Icon: AlertCircle, tone: 'bg-rose-50 border-rose-200 text-rose-800' },
  };
  const m = kindMeta[event.kind] || { Icon: TrendingUp, tone: 'bg-slate-50 border-slate-200 text-slate-700' };
  const Icon = m.Icon;
  return (
    <div className={`flex items-start gap-2 rounded-lg p-2 border ${m.tone}`} data-testid={`srv-tl-${event.kind}-${event.id}`}>
      <Icon size={14} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold truncate">{event.label}</div>
        <div className="text-[10px] opacity-75">{fmtDate(event.ts)} · {event.status || '—'}</div>
      </div>
    </div>
  );
}

// =====================================================
// INVOICE INFO PANEL — LEFT-side nested slide-in
// Shows invoice details as info (NOT PDF — that's done elsewhere).
// =====================================================
function InvoiceInfoPanel({ invoice, onClose }) {
  return (
    <div className="fixed inset-0 z-[85] bg-black/30 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="srv-inv-info">
      <div className="absolute left-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-2xl overflow-y-auto">
        <div className="px-5 py-3.5 bg-emerald-700 text-white flex items-center justify-between sticky top-0">
          <div>
            <div className="text-xs opacity-90">Invoice Details</div>
            <div className="font-bold font-mono">{invoice.invoice_number}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row label="Type" value={invoice.is_gst ? 'GST Invoice' : 'Estimate / Non-GST'} />
          <Row label="Status" value={<StatusChip status={invoice.payment_status} />} />
          <Row label="Customer" value={invoice.customer_name} />
          <Row label="Mobile" value={invoice.customer_phone} />
          {invoice.customer_whatsapp && invoice.customer_whatsapp !== invoice.customer_phone && <Row label="WhatsApp" value={invoice.customer_whatsapp} />}
          {invoice.customer_email && <Row label="Email" value={invoice.customer_email} />}
          {invoice.customer_address && <Row label="Address" value={invoice.customer_address} />}
          <Row label="Service" value={invoice.service_name || '—'} />
          <Row label="Service Start" value={fmtDate(invoice.service_start_date || invoice.service_date)} />
          {invoice.service_end_date && <Row label="Service End" value={fmtDate(invoice.service_end_date)} />}
          {invoice.booking_id && <Row label="Linked Booking" value={invoice.booking_id} />}
          {(invoice.pest_types || []).length > 0 && <Row label="Pests" value={(invoice.pest_types || []).join(', ')} />}
          <Row label="Invoice Date" value={fmtDate(invoice.invoice_date)} />
          <Row label="Due Date" value={fmtDate(invoice.due_date)} />
          <Row label="Reference No" value={invoice.reference_no} />
          <Row label="Client Order No" value={invoice.client_order_no} />
          <Row label="Dispatch Doc No" value={invoice.dispatch_doc_no} />

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr><th className="text-left px-2 py-1.5">Service / Pest</th><th className="text-right px-2 py-1.5 w-12">Qty</th><th className="text-right px-2 py-1.5 w-16">Rate</th><th className="text-right px-2 py-1.5 w-20">Amount</th></tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">
                      <div className="font-semibold">{it.description}</div>
                      {(it.pest_names || []).length > 0 && <div className="text-[10px] text-slate-500">Pests: {it.pest_names.join(', ')}{it.pest_other && ` · ${it.pest_other}`}</div>}
                      {it.property_size && <div className="text-[10px] text-slate-500">{it.property_size}{it.area_sqft ? ` · ${it.area_sqft} sqft` : ''}{it.warranty_value ? ` · Warranty: ${it.warranty_value} ${it.warranty_unit}` : ''}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right">{it.quantity}</td>
                    <td className="px-2 py-1.5 text-right">{fmtMoney(it.rate)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmtMoney(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 space-y-1">
            <Row label="Subtotal" value={fmtMoney(invoice.subtotal)} compact />
            <Row label="Discount" value={`– ${fmtMoney(invoice.discount)}`} compact />
            <Row label={`GST (${invoice.gst_rate || 0}%)`} value={fmtMoney(invoice.gst_amount)} compact />
            <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1.5"><span>Total</span><span>{fmtMoney(invoice.total_amount)}</span></div>
            <Row label="Amount Paid" value={fmtMoney(invoice.amount_paid)} compact />
            <Row label="Amount Due" value={fmtMoney(invoice.amount_due)} compact />
          </div>

          {invoice.notes && <Row label="Notes" value={invoice.notes} />}
          {invoice.terms && <Row label="Terms" value={invoice.terms} />}
          <div className="text-[10px] text-slate-400 pt-2 border-t">This is an info-only view. To download the PDF, use the Invoices tab.</div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, compact }) {
  return (
    <div className={`flex justify-between ${compact ? 'text-xs' : 'text-sm'} gap-3`}>
      <span className="text-slate-500 flex-shrink-0">{label}</span>
      <span className="font-medium text-right break-words">{value || '—'}</span>
    </div>
  );
}
